import { z } from "zod";

/**
 * Single source of truth for the /contatti form: the client validates with
 * this schema and /api/contact re-validates with the same rules, so a message
 * that passes in the browser can never be rejected by the server for length.
 */

export const contactNeedValues = [
  "new_website",
  "website_redesign",
  "mvp",
  "ai_automation",
  "support_maintenance",
  "other",
] as const;

export type ContactNeed = (typeof contactNeedValues)[number];

export const MESSAGE_MIN = 10;
export const MESSAGE_MAX = 3000;

// Single-line fields end up in email headers (Subject, Reply-To): no CR/LF.
const SINGLE_LINE = /^[^\u0000-\u001F\u007F]*$/;

export const contactFormSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, "Scrivi il tuo nome, così so come chiamarti.")
    .max(100, "Il nome è troppo lungo (massimo 100 caratteri).")
    .regex(SINGLE_LINE, "Il nome contiene caratteri non validi."),
  email: z
    .string()
    .trim()
    .min(1, "Scrivi la tua email, è dove ti rispondo.")
    .max(255, "L'email è troppo lunga.")
    .email(
      "Controlla l'email: manca la chiocciola o il dominio (esempio: nome@azienda.it).",
    ),
  phone: z
    .string()
    .trim()
    .max(40, "Il numero è troppo lungo.")
    .refine(
      (v) => v === "" || /^[0-9+()\s.-]{6,}$/.test(v),
      "Il numero sembra incompleto. Puoi anche lasciare il campo vuoto.",
    )
    .default(""),
  need: z.enum(contactNeedValues, {
    errorMap: () => ({
      message: 'Scegli l\'opzione più vicina. Va bene anche "Altro, ne parliamo".',
    }),
  }),
  message: z
    .string()
    .trim()
    .min(
      MESSAGE_MIN,
      "Scrivi almeno una riga (10 caratteri), così posso risponderti con qualcosa di utile.",
    )
    .max(MESSAGE_MAX, "Il messaggio è troppo lungo (massimo 3000 caratteri)."),
});

export type ContactFormData = z.infer<typeof contactFormSchema>;
export type ContactField = keyof ContactFormData;
